import { ReactNode } from "react";
import { SxProps, Theme } from "@mui/material";
import { ISearchUnderlying, ISearchWikifolio } from "src/api/client/search-autocomplete.api.types";
import { useTypeaheadEventHandlers } from "src/components-mui/common/typeahead/hooks/use-typeahead-event-handlers";
import { ITypeaheadState, useTypeaheadState } from "src/components-mui/common/typeahead/hooks/use-typeahead-state";
import { TypeaheadUI } from "src/components-mui/common/typeahead/typeahead-ui";
import { IPlacement } from "src/components-mui/common/typeahead/typeahead.types";

export interface ITypeaheadWrapper extends Pick<ITypeahead<ISearchWikifolio | ISearchUnderlying>, "placeholderText" | "enableAutoFocus" | "onFocus"> {
    placement?: IPlacement;
}

export interface ITypeahead<TResultItem> {
    placeholderText: string;
    enableAutoFocus?: boolean;
    combinedResults: ReadonlyArray<TResultItem>;
    query: string;
    isDisabled?: boolean;
    setQuery: (query: string) => void;
    onClear: () => void;
    onFocus?: () => void;
    onSelect: (selectedItem: TResultItem, selectedIndex: number) => void;
    onSubmit?: () => void;
    children: (state: ITypeaheadState) => ReactNode;
    sx?: SxProps<Theme>;
}

export const Typeahead = <TResultItem,>({
    placeholderText,
    enableAutoFocus,
    combinedResults,
    query,
    setQuery,
    onClear,
    onFocus,
    onSelect,
    onSubmit,
    children,
    isDisabled,
    sx,
}: ITypeahead<TResultItem>) => {
    const state = useTypeaheadState();
    const { handleSearchChange, handleFocus, handleClear, handleClose, handleKeyDown, handleSubmit } = useTypeaheadEventHandlers({
        combinedResults,
        state,
        events: { setQuery, onClear, onFocus, onSelect, onSubmit },
    });

    return (
        <TypeaheadUI
            onChange={handleSearchChange}
            enableAutoFocus={enableAutoFocus}
            onFocus={handleFocus}
            query={query}
            placeholderText={placeholderText}
            onClear={handleClear}
            onClose={handleClose}
            onKeyDown={handleKeyDown}
            onSubmit={handleSubmit}
            isDisabled={isDisabled}
            sx={sx}
        >
            {children(state)}
        </TypeaheadUI>
    );
};
